import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useTheme } from '../App';
import { ThreeBackground } from '../components/ThreeBackground';
import { FloatingElements } from '../components/FloatingElements';
import { BurgerMenu } from '../components/BurgerMenu';
import { Magnetic } from '../components/ui/Magnetic';
import { LazyImage } from '../components/LazyImage';
interface ProjectsPageProps {
  projects: any[];
  onGoBack: () => void;
  onGoToAdmin: () => void;
}
export function ProjectsPage({
  projects,
  onGoBack,
  onGoToAdmin
}: ProjectsPageProps) {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <div className={`min-h-screen selection:bg-white selection:text-black overflow-x-hidden transition-colors duration-500 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`} data-id="element-478">
      {/* Three.js Background */}
      <div className="fixed inset-0 z-0" data-id="element-479">
        <ThreeBackground data-id="element-480" />
      </div>

      <FloatingElements data-id="element-481" />
      <BurgerMenu onGoToAdmin={onGoToAdmin} onGoToProjects={onGoBack} onGoHome={onGoBack} currentPage="projects" data-id="element-482" />

      {/* Navigation - Same as HomePage */}
      <nav className="fixed top-0 left-0 w-full p-8 flex justify-between items-center z-[30] pointer-events-none" data-id="element-483">
        <div className="pointer-events-auto" data-id="element-484">
          <button onClick={onGoBack} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-485">
            Jav.
          </button>
        </div>
      </nav>

      <main className="relative z-10 min-h-screen flex flex-col" data-id="element-486">
        {/* Main Content */}
        <section className="flex-grow py-32 px-4 md:px-12" data-id="element-487">
          <div className="max-w-7xl mx-auto" data-id="element-488">
            {/* Header */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="mb-20" data-id="element-489">
              <motion.span className="text-sm mb-6 block uppercase tracking-widest font-light" style={{
              color: isDark ? '#999' : '#666'
            }} data-id="element-490">
                Selected Work
              </motion.span>
              <h1 className={`text-6xl md:text-8xl font-black tracking-tighter ${isDark ? 'text-white' : 'text-black'}`} data-id="element-491">
                Projects
              </h1>
              <p className="text-xl md:text-2xl mt-6 max-w-2xl" style={{
              color: isDark ? 'rgb(163, 163, 163)' : '#666'
            }} data-id="element-492">
                A collection of digital experiences exploring the intersection
                of art, technology, and human creativity.
              </p>
            </motion.div>

            {/* Projects Grid */}
            {projects.length === 0 ? <div className="text-center py-16" data-id="element-493">
                <p className="text-xl" style={{
              color: isDark ? 'rgb(163, 163, 163)' : '#666'
            }} data-id="element-494">
                  No projects yet. Add some in the Admin panel.
                </p>
              </div> : <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-495">
                {projects.map(project => <motion.div key={project.id} variants={cardVariants} className="group" data-id="element-496">
                    <div className={`relative overflow-hidden rounded-lg border transition-all duration-500 ${isDark ? 'bg-neutral-900/50 border-white/10 hover:border-white/30' : 'bg-white/50 border-black/10 hover:border-black/30'}`} data-id="element-497">
                      {/* Image */}
                      <div className="relative w-full overflow-hidden" data-id="element-498">
                        <motion.div whileHover={{
                    scale: 1.02
                  }} transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1]
                  }} data-id="element-499">
                          <LazyImage src={project.image} alt={project.title} className="w-full h-auto object-contain" data-id="element-500" />
                        </motion.div>
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                    background: isDark ? 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 50%)' : 'linear-gradient(to top, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%)'
                  }} data-id="element-501" />
                      </div>

                      {/* Content */}
                      <div className="p-8" data-id="element-502">
                        {/* Category Label */}
                        {project.category && <span className="inline-block px-3 py-1 rounded-full text-xs uppercase tracking-wider font-medium mb-4" style={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    color: isDark ? 'rgb(163, 163, 163)' : '#666'
                  }} data-id="element-503">
                            {project.category}
                          </span>}

                        <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-504">
                          {project.title}
                        </h3>
                        <p className="text-base mb-6 leading-relaxed" style={{
                    color: isDark ? 'rgb(163, 163, 163)' : '#666'
                  }} data-id="element-505">
                          {project.description}
                        </p>

                        {/* External Link Button */}
                        {project.button_disabled || project.buttonDisabled || !project.link ? <button disabled className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium cursor-not-allowed ${isDark ? 'bg-white/30 text-white/50' : 'bg-black/30 text-black/50'}`} data-id="element-506">
                            {project.button_text || project.buttonText || 'View Project'}
                            <ArrowUpRight className="w-4 h-4" data-id="element-507" />
                          </button> : <a href={project.link} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${isDark ? 'bg-white text-black hover:bg-neutral-200' : 'bg-black text-white hover:bg-neutral-800'}`} data-id="element-508">
                            {project.button_text || project.buttonText || 'View Project'}
                            <ArrowUpRight className="w-4 h-4" data-id="element-509" />
                          </a>}
                      </div>
                    </div>
                  </motion.div>)}
              </motion.div>}
          </div>
        </section>

        {/* Simple Footer */}
        <footer className="relative z-10 w-full py-8 px-4 md:px-12 border-t" style={{
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
      }} data-id="element-510">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm uppercase tracking-wide font-light" style={{
          color: isDark ? '#999' : '#666'
        }} data-id="element-511">
            <span data-id="element-512">
              © {new Date().getFullYear()} Javstuff.com — Buenos Aires, AR
            </span>
            <span data-id="element-513">All Rights Reserved</span>
          </div>
        </footer>
      </main>
    </div>;
}